import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  ShieldAlert,
  Loader2,
  RefreshCw,
  Search,
  Filter,
  LogIn,
  LogOut,
  KeyRound,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatApiErrorDetail } from "@/lib/apiError";

const PAGE_SIZE = 25;

const EVENT_META = {
  login_success: {
    icon: LogIn,
    color: "#7CC57F",
    label_fr: "Connexion réussie",
    label_en: "Login success",
  },
  login_failure: {
    icon: ShieldAlert,
    color: "#E27474",
    label_fr: "Échec de connexion",
    label_en: "Login failure",
  },
  login_2fa_success: {
    icon: ShieldCheck,
    color: "#7CC57F",
    label_fr: "2FA validée",
    label_en: "2FA verified",
  },
  login_2fa_failure: {
    icon: ShieldAlert,
    color: "#E27474",
    label_fr: "Échec 2FA",
    label_en: "2FA failure",
  },
  password_reset: {
    icon: KeyRound,
    color: "#C5A059",
    label_fr: "Mot de passe réinitialisé",
    label_en: "Password reset",
  },
  logout: {
    icon: LogOut,
    color: "#A19E9C",
    label_fr: "Déconnexion",
    label_en: "Logout",
  },
};

const REASON_LABELS = {
  invalid_credentials: { fr: "Identifiants invalides", en: "Invalid credentials" },
  throttled: { fr: "Bloqué (trop d'essais)", en: "Throttled (too many tries)" },
  invalid_code: { fr: "Code 2FA invalide", en: "Invalid 2FA code" },
  invalid_2fa_setup: { fr: "Configuration 2FA invalide", en: "Invalid 2FA setup" },
  password_ok_pending_2fa: { fr: "Mot de passe OK, 2FA en attente", en: "Password OK, 2FA pending" },
  totp: { fr: "Code TOTP", en: "TOTP code" },
  recovery_code: { fr: "Code de récupération", en: "Recovery code" },
};

const formatDate = (iso, lang) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(lang === "fr" ? "fr-FR" : "en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
};

const parseUserAgent = (ua) => {
  if (!ua) return "—";
  // Best-effort short label — full UA shown in tooltip
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS";
  if (/Android/i.test(ua)) return "Android";
  if (/Mac OS X/i.test(ua)) return "macOS";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Linux/i.test(ua)) return "Linux";
  if (/curl|Postman|python|axios|wget/i.test(ua)) return "API client";
  return "Web";
};

const AuditLogPanel = () => {
  const { authedAxios } = useAuth();
  const { lang } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [emailFilter, setEmailFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [onlyFailures, setOnlyFailures] = useState(false);
  const [page, setPage] = useState(0);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const ax = authedAxios();
      const { data } = await ax.get("/admin/audit-log", {
        params: {
          limit: PAGE_SIZE,
          skip: page * PAGE_SIZE,
          email: emailFilter || undefined,
          only_failures: onlyFailures || undefined,
        },
      });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStats(data.stats || null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err));
    } finally {
      setLoading(false);
    }
  }, [authedAxios, page, emailFilter, onlyFailures]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const canPrev = page > 0;
  const canNext = page + 1 < totalPages;

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(0);
    setEmailFilter(searchInput.trim().toLowerCase());
  };

  const handleClearFilters = () => {
    setSearchInput("");
    setEmailFilter("");
    setOnlyFailures(false);
    setPage(0);
  };

  const reasonText = useMemo(() => (
    (reason) => {
      if (!reason) return null;
      const meta = REASON_LABELS[reason];
      return meta ? meta[lang] : reason;
    }
  ), [lang]);

  return (
    <section data-testid="admin-audit-panel" className="space-y-10">
      <div>
        <p className="overline kw-accent-line">
          {lang === "fr" ? "Sécurité" : "Security"}
        </p>
        <h2 className="font-serif-display text-4xl md:text-5xl mt-4">
          {lang === "fr" ? "Journal des connexions" : "Login audit log"}
        </h2>
        <p className="mt-3 text-sm text-[#A19E9C] max-w-2xl">
          {lang === "fr"
            ? "Historique de toutes les tentatives d'authentification admin — connexions réussies, échecs, 2FA et réinitialisations de mot de passe."
            : "History of every admin authentication attempt — successful logins, failures, 2FA events and password resets."}
        </p>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatCard
            data-testid="audit-stat-total"
            icon={ShieldCheck}
            label={lang === "fr" ? "Événements (24h)" : "Events (24h)"}
            value={stats.last_24h_total}
            color="#C5A059"
          />
          <StatCard
            data-testid="audit-stat-failures"
            icon={AlertTriangle}
            label={lang === "fr" ? "Échecs (24h)" : "Failures (24h)"}
            value={stats.last_24h_failures}
            color={stats.last_24h_failures > 0 ? "#E27474" : "#7CC57F"}
          />
          <StatCard
            data-testid="audit-stat-ips"
            icon={Filter}
            label={lang === "fr" ? "IPs uniques (24h)" : "Unique IPs (24h)"}
            value={stats.last_24h_unique_ips}
            color="#F2EBE5"
          />
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
        <form onSubmit={handleSearch} className="flex-1 flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-[#A19E9C] absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
            <input
              type="email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder={lang === "fr" ? "Filtrer par email…" : "Filter by email…"}
              data-testid="audit-email-filter"
              className="w-full kw-input pl-9"
            />
          </div>
          <button
            type="submit"
            data-testid="audit-search-btn"
            className="kw-btn-ghost"
          >
            {lang === "fr" ? "Filtrer" : "Filter"}
          </button>
        </form>

        <button
          type="button"
          onClick={() => {
            setOnlyFailures((v) => !v);
            setPage(0);
          }}
          data-testid="audit-failures-toggle"
          className={`text-[11px] uppercase tracking-[0.25em] px-4 py-2.5 border transition-colors whitespace-nowrap ${
            onlyFailures
              ? "border-[#E27474] text-[#E27474] bg-[#E27474]/5"
              : "border-[#F2EBE5]/15 text-[#A19E9C] hover:text-[#F2EBE5]"
          }`}
        >
          <AlertTriangle className="w-3.5 h-3.5 inline mr-2" strokeWidth={1.5} />
          {lang === "fr" ? "Échecs uniquement" : "Failures only"}
        </button>

        <button
          type="button"
          onClick={fetchData}
          disabled={loading}
          data-testid="audit-refresh-btn"
          className="kw-btn-ghost"
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
          ) : (
            <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
          )}
          {lang === "fr" ? "Actualiser" : "Refresh"}
        </button>

        {(emailFilter || onlyFailures) && (
          <button
            type="button"
            onClick={handleClearFilters}
            data-testid="audit-clear-filters"
            className="text-[10px] uppercase tracking-[0.2em] text-[#A19E9C] hover:text-[#C5A059] transition-colors"
          >
            {lang === "fr" ? "Effacer" : "Clear"}
          </button>
        )}
      </div>

      {/* Table */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="border border-[#F2EBE5]/10 bg-[#0a0a0a] overflow-hidden"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="audit-log-table">
            <thead>
              <tr className="border-b border-[#F2EBE5]/10 bg-[#050505]">
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-[#A19E9C] font-normal">
                  {lang === "fr" ? "Événement" : "Event"}
                </th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-[#A19E9C] font-normal">
                  {lang === "fr" ? "Email" : "Email"}
                </th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-[#A19E9C] font-normal hidden md:table-cell">
                  {lang === "fr" ? "IP" : "IP"}
                </th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-[#A19E9C] font-normal hidden lg:table-cell">
                  {lang === "fr" ? "Appareil" : "Device"}
                </th>
                <th className="text-left px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-[#A19E9C] font-normal hidden md:table-cell">
                  {lang === "fr" ? "Détail" : "Reason"}
                </th>
                <th className="text-right px-4 py-3 text-[10px] uppercase tracking-[0.25em] text-[#A19E9C] font-normal">
                  {lang === "fr" ? "Date" : "Date"}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-[#A19E9C]">
                    <Loader2 className="w-5 h-5 animate-spin inline mr-2" strokeWidth={1.5} />
                    {lang === "fr" ? "Chargement…" : "Loading…"}
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-12 text-center text-[#A19E9C]" data-testid="audit-empty-state">
                    {lang === "fr"
                      ? "Aucun événement à afficher."
                      : "No events to display."}
                  </td>
                </tr>
              )}
              {items.map((row) => {
                const meta = EVENT_META[row.event_type] || {
                  icon: ShieldCheck,
                  color: "#A19E9C",
                  label_fr: row.event_type,
                  label_en: row.event_type,
                };
                const Icon = meta.icon;
                return (
                  <tr
                    key={row.id}
                    data-testid="audit-row"
                    data-event={row.event_type}
                    className="border-b border-[#F2EBE5]/5 hover:bg-[#050505] transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Icon
                          className="w-4 h-4 flex-shrink-0"
                          strokeWidth={1.5}
                          style={{ color: meta.color }}
                        />
                        <span
                          className="text-[12px]"
                          style={{ color: row.success ? "#F2EBE5" : "#E27474" }}
                        >
                          {lang === "fr" ? meta.label_fr : meta.label_en}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[#F2EBE5] text-[12px] break-all">
                      {row.email || "—"}
                    </td>
                    <td className="px-4 py-3 text-[#A19E9C] text-[11px] font-mono hidden md:table-cell">
                      {row.ip || "—"}
                    </td>
                    <td className="px-4 py-3 text-[#A19E9C] text-[11px] hidden lg:table-cell">
                      <span title={row.user_agent || ""}>
                        {parseUserAgent(row.user_agent)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#A19E9C] text-[11px] hidden md:table-cell">
                      {reasonText(row.reason) || "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-[#A19E9C] text-[11px] whitespace-nowrap">
                      {formatDate(row.created_at, lang)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-[#F2EBE5]/10 text-[11px] uppercase tracking-[0.2em] text-[#A19E9C]">
            <span data-testid="audit-pagination-info">
              {lang === "fr"
                ? `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} sur ${total}`
                : `${page * PAGE_SIZE + 1}–${Math.min((page + 1) * PAGE_SIZE, total)} of ${total}`}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => canPrev && setPage((p) => p - 1)}
                disabled={!canPrev}
                data-testid="audit-prev-page"
                className="p-1.5 border border-[#F2EBE5]/15 hover:text-[#C5A059] hover:border-[#C5A059]/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="prev"
              >
                <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
              </button>
              <span className="px-2">
                {page + 1} / {totalPages}
              </span>
              <button
                type="button"
                onClick={() => canNext && setPage((p) => p + 1)}
                disabled={!canNext}
                data-testid="audit-next-page"
                className="p-1.5 border border-[#F2EBE5]/15 hover:text-[#C5A059] hover:border-[#C5A059]/40 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                aria-label="next"
              >
                <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </section>
  );
};

const StatCard = ({ icon: Icon, label, value, color, ...rest }) => (
  <div
    {...rest}
    className="border border-[#F2EBE5]/10 bg-[#0a0a0a] p-5"
  >
    <div className="flex items-center justify-between">
      <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color }} />
      <span className="text-[9px] uppercase tracking-[0.3em] text-[#A19E9C]">
        {label}
      </span>
    </div>
    <p
      className="mt-4 font-serif-display text-3xl"
      style={{ color }}
    >
      {value}
    </p>
  </div>
);

export default AuditLogPanel;
